/**
 * Customer Intelligence Context
 *
 * This context manages SMART tracking for the AI sales assistant:
 * - Browser fingerprinting for customer recognition
 * - Real-time browsing behavior tracking
 * - Session and page view tracking
 * - Cart activity monitoring
 * - Search history
 *
 * All data is used to provide personalized AI recommendations.
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { generateFingerprint, getDeviceMetadata } from '../services/fingerprint';
import { useAuth } from './AuthContext';
import { apiClient } from '../api';

// Fire-and-forget tracking request (no await, no error blocking)
function trackEvent(endpoint: string, data: Record<string, unknown>): void {
  // Use the apiClient's trackEvent method for fire-and-forget tracking
  apiClient.trackEvent(endpoint, data);
}

// Types
interface BrowsingEvent {
  type: 'page_view' | 'product_view' | 'search' | 'add_to_cart' | 'remove_from_cart';
  timestamp: number;
  data: Record<string, unknown>;
}

interface CustomerProfile {
  id?: string;
  fingerprintHash: string;
  customerId?: string;
  email?: string;
  firstName?: string;
  sessionCount: number;
  lastSeenAt: string;
  firstSeenAt: string;
}

interface RealtimeContext {
  currentPage: string;
  currentProductId?: string;
  sessionDuration: number;
  pageViewCount: number;
  recentActions: BrowsingEvent[];
}

interface SessionInfo {
  sessionCount: number;
  sessionDuration: number;
}

interface CustomerIntelligenceContextType {
  // Profile data
  profile: CustomerProfile | null;
  isLoading: boolean;
  fingerprint: string | null;
  sessionInfo: SessionInfo;

  // Real-time context for AI
  realtimeContext: RealtimeContext;

  // Tracking methods
  trackProductView: (productId: string, productData: Record<string, unknown>) => void;
  trackSearch: (query: string, resultsCount: number) => void;
  trackAddToCart: (productId: string, variantId: string, quantity: number) => void;
  trackRemoveFromCart: (productId: string, variantId: string) => void;

  // AI context payload
  getAIContextPayload: () => AIContextPayload;
}

interface AIContextPayload {
  fingerprint: string;
  customerId?: string;
  currentPage: string;
  currentProductId?: string;
  sessionDuration: number;
  recentActions: BrowsingEvent[];
  deviceType: string;
  browser: string;
}

const CustomerIntelligenceContext = createContext<CustomerIntelligenceContextType | undefined>(undefined);

// Constants
const MAX_RECENT_ACTIONS = 20;
const SESSION_STORAGE_KEY = 'petmas_session';
const PROFILE_SYNC_INTERVAL_MS = 60000; // Sync profile every minute
const DEFAULT_SESSION_COUNT = 1;
const MS_PER_SECOND = 1000;

export function CustomerIntelligenceProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { customer } = useAuth();

  // State
  const [profile, setProfile] = useState<CustomerProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [recentActions, setRecentActions] = useState<BrowsingEvent[]>([]);
  const [pageViewCount, setPageViewCount] = useState(0);
  const [currentProductId, setCurrentProductId] = useState<string | undefined>();

  // Refs
  const sessionStartRef = useRef<number>(Date.now());
  const fingerprintRef = useRef<string | null>(null);
  const deviceMetadataRef = useRef<ReturnType<typeof getDeviceMetadata> | null>(null);
  const syncIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Initialize fingerprint and profile
  useEffect(() => {
    async function initialize() {
      try {
        // Generate fingerprint
        const fingerprint = await generateFingerprint();
        fingerprintRef.current = fingerprint.hash;
        deviceMetadataRef.current = getDeviceMetadata();

        // Load or create session
        const sessionData = loadSession();
        if (sessionData) {
          sessionStartRef.current = sessionData.startTime;
          setPageViewCount(sessionData.pageViewCount);
        } else {
          saveSession({
            startTime: sessionStartRef.current,
            pageViewCount: 0,
            fingerprintHash: fingerprint.hash,
          });
        }

        // Fetch or create customer profile from backend
        await syncProfile(fingerprint.hash, customer?.id);

        setIsLoading(false);
      } catch (error) {
        console.error('[CustomerIntelligence] Initialization error:', error);
        setIsLoading(false);
      }
    }

    initialize();

    // Set up periodic profile sync
    syncIntervalRef.current = setInterval(() => {
      if (fingerprintRef.current) {
        syncProfile(fingerprintRef.current, customer?.id);
      }
    }, PROFILE_SYNC_INTERVAL_MS);

    return () => {
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
      }
    };
  }, [customer?.id]);

  // Track page views
  useEffect(() => {
    setPageViewCount((prev) => prev + 1);
    setCurrentProductId(undefined);

    // Update session
    const session = loadSession();
    if (session) {
      saveSession({ ...session, pageViewCount: session.pageViewCount + 1 });
    }

    // Track page view event
    addEvent({
      type: 'page_view',
      timestamp: Date.now(),
      data: { path: location.pathname },
    });
  }, [location.pathname]);

  // Sync profile with backend
  async function syncProfile(fingerprintHash: string, customerId?: string) {
    try {
      const result = await apiClient.syncCustomerProfile(
        fingerprintHash,
        customerId,
        deviceMetadataRef.current ?? undefined
      );

      if (result.profile) {
        setProfile({
          id: result.profile.id,
          fingerprintHash: result.profile.fingerprintHash,
          customerId: result.profile.customerId,
          sessionCount: result.profile.sessionCount,
          lastSeenAt: result.profile.lastSeenAt,
          firstSeenAt: result.profile.firstSeenAt,
        });
        return;
      }
    } catch (error) {
      console.warn('[CustomerIntelligence] Profile sync failed:', error);
    }

    // Create local profile if backend unavailable
    setProfile({
      fingerprintHash,
      customerId,
      sessionCount: DEFAULT_SESSION_COUNT,
      lastSeenAt: new Date().toISOString(),
      firstSeenAt: new Date().toISOString(),
    });
  }

  // Add event to recent actions
  const addEvent = useCallback((event: BrowsingEvent) => {
    setRecentActions((prev) => {
      const updated = [event, ...prev];
      return updated.slice(0, MAX_RECENT_ACTIONS);
    });
  }, []);

  // Tracking methods
  const trackProductView = useCallback((productId: string, productData: Record<string, unknown>) => {
    setCurrentProductId(productId);
    addEvent({
      type: 'product_view',
      timestamp: Date.now(),
      data: { productId, ...productData },
    });

    // Send to backend asynchronously (fire-and-forget)
    if (fingerprintRef.current) {
      trackEvent('/intelligence/track/product-view', {
        fingerprintHash: fingerprintRef.current,
        productId,
        productData,
      });
    }
  }, [addEvent]);

  const trackSearch = useCallback((query: string, resultsCount: number) => {
    addEvent({
      type: 'search',
      timestamp: Date.now(),
      data: { query, resultsCount },
    });

    // Send to backend (fire-and-forget)
    if (fingerprintRef.current) {
      trackEvent('/intelligence/track/search', {
        fingerprintHash: fingerprintRef.current,
        query,
        resultsCount,
      });
    }
  }, [addEvent]);

  const trackAddToCart = useCallback((productId: string, variantId: string, quantity: number) => {
    addEvent({
      type: 'add_to_cart',
      timestamp: Date.now(),
      data: { productId, variantId, quantity },
    });

    // Send to backend (fire-and-forget)
    if (fingerprintRef.current) {
      trackEvent('/intelligence/track/cart', {
        fingerprintHash: fingerprintRef.current,
        action: 'add',
        productId,
        variantId,
        quantity,
      });
    }
  }, [addEvent]);

  const trackRemoveFromCart = useCallback((productId: string, variantId: string) => {
    addEvent({
      type: 'remove_from_cart',
      timestamp: Date.now(),
      data: { productId, variantId },
    });

    // Send to backend (fire-and-forget)
    if (fingerprintRef.current) {
      trackEvent('/intelligence/track/cart', {
        fingerprintHash: fingerprintRef.current,
        action: 'remove',
        productId,
        variantId,
      });
    }
  }, [addEvent]);

  // Get AI context payload
  const getAIContextPayload = useCallback((): AIContextPayload => {
    return {
      fingerprint: fingerprintRef.current ?? 'unknown',
      customerId: customer?.id,
      currentPage: location.pathname,
      currentProductId,
      sessionDuration: Math.floor((Date.now() - sessionStartRef.current) / MS_PER_SECOND),
      recentActions,
      deviceType: deviceMetadataRef.current?.deviceType ?? 'unknown',
      browser: deviceMetadataRef.current?.browser ?? 'unknown',
    };
  }, [customer?.id, location.pathname, currentProductId, recentActions]);

  // Real-time context
  const realtimeContext: RealtimeContext = {
    currentPage: location.pathname,
    currentProductId,
    sessionDuration: Math.floor((Date.now() - sessionStartRef.current) / MS_PER_SECOND),
    pageViewCount,
    recentActions,
  };

  return (
    <CustomerIntelligenceContext.Provider
      value={{
        profile,
        isLoading,
        fingerprint: fingerprintRef.current,
        sessionInfo: {
          sessionCount: profile?.sessionCount ?? DEFAULT_SESSION_COUNT,
          sessionDuration: Math.floor((Date.now() - sessionStartRef.current) / MS_PER_SECOND),
        },
        realtimeContext,
        trackProductView,
        trackSearch,
        trackAddToCart,
        trackRemoveFromCart,
        getAIContextPayload,
      }}
    >
      {children}
    </CustomerIntelligenceContext.Provider>
  );
}

export function useCustomerIntelligence(): CustomerIntelligenceContextType {
  const context = useContext(CustomerIntelligenceContext);
  if (context === undefined) {
    throw new Error('useCustomerIntelligence must be used within a CustomerIntelligenceProvider');
  }
  return context;
}

// Session storage helpers
interface SessionData {
  startTime: number;
  pageViewCount: number;
  fingerprintHash: string;
}

function loadSession(): SessionData | null {
  try {
    const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!stored) return null;
    return JSON.parse(stored) as SessionData;
  } catch {
    return null;
  }
}

function saveSession(data: SessionData): void {
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(data));
  } catch {
    console.warn('[CustomerIntelligence] Failed to save session');
  }
}
